import React, { useState } from 'react';
import { Modal } from '../Modal';
import { JOB_LABEL, cn } from '../model/derive';
import { dur } from '../model/dashboard';
import { ACCEPT_FAIL_MIN, DROP_RULES, DUE_REASONS, dueOptions, kstAt, reassignCandidates, receiverCandidates } from './liveBoardModel';
import { PillButton } from './opsUi';

/**
 * Live board 운영진 대화상자 (시안 dlg) — 제목 · 설명 · 현재 값 상자 · 고르기(알약 select) · 라디오(설명 포함) · 입력 · 안내 상자 · Close / 실행.
 * 필수가 비면 실행 버튼이 꺼지고 그 이유가 툴팁으로. 처리는 부모의 onAct(action) → 서버 → 보드 새로고침 + 기록 + 토스트. 성공하면 닫힌다.
 */

const firstName = (name) => (name ?? '').split(' ')[0];

const useSubmit = (onAct, onClose) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (action) => {
    setBusy(true);
    setError('');
    const ok = await onAct(action);
    setBusy(false);
    if (ok) onClose();
    else setError('Couldn’t save. Try again.');
  };
  return { busy, error, submit };
};

const Cur = ({ rows }) => (
  <div className='grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 rounded-xl bg-green-deep03/[0.04] px-3.5 py-3 text-[14px] leading-[140%]'>
    {rows.map(([k, v]) => (
      <React.Fragment key={k}>
        <span className='text-text-secondary'>{k}</span>
        <span className='font-medium tabular-nums text-text-primary'>{v}</span>
      </React.Fragment>
    ))}
  </div>
);
const Label = ({ children }) => <span className='text-[13px] font-medium leading-[140%] text-text-primary'>{children}</span>;
const SelectPill = ({ label, value, onChange, placeholder, options, disabled }) => (
  <label className='flex flex-col gap-1.5'>
    <Label>{label}</Label>
    <span className='relative'>
      <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} className='h-11 w-full appearance-none rounded-full border border-green-deep03/20 bg-grayscale-gray00 pl-4 pr-10 text-[14px] text-text-primary focus:border-green-deep02 focus:outline-none disabled:text-text-disable'>
        <option value=''>{placeholder}</option>
        {options.map((o) => (
          <option key={o.v} value={o.v}>
            {o.l}
          </option>
        ))}
      </select>
      <svg viewBox='0 0 16 16' fill='none' aria-hidden className='pointer-events-none absolute right-4 top-[13px] h-[18px] w-[18px] text-icon-secondary'>
        <path d='M4 6l4 4 4-4' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
      </svg>
    </span>
  </label>
);
const RadioList = ({ label, value, onChange, options }) => (
  <div role='radiogroup' aria-label={label} className='flex flex-col gap-1.5'>
    <Label>{label}</Label>
    <div className='flex flex-col overflow-hidden rounded-xl border border-green-deep03/[0.12]'>
      {options.map((o, i) => {
        const on = value === o.key;
        return (
          <button key={o.key} type='button' role='radio' aria-checked={on} onClick={() => onChange(o.key)} className={cn('grid grid-cols-[20px_minmax(0,1fr)] items-start gap-3 px-3.5 py-3 text-left', i > 0 && 'border-t border-green-deep03/[0.12]', on && 'bg-surface-green10')}>
            <span className={cn('mt-px box-border flex h-5 w-5 items-center justify-center rounded-full border-[1.6px]', on ? 'border-green-deep02' : 'border-grayscale-gray04')}>
              <span className={cn('h-2.5 w-2.5 rounded-full', on ? 'bg-green-deep02' : 'bg-transparent')} />
            </span>
            <span className='flex flex-col gap-0.5'>
              <span className='text-[14px] font-medium leading-[140%] text-text-primary'>{o.label}</span>
              {o.desc && <span className='text-[13px] leading-[140%] text-text-secondary'>{o.desc}</span>}
            </span>
          </button>
        );
      })}
    </div>
  </div>
);
const Input = ({ label, value, onChange, placeholder, autoFocus }) => (
  <label className='flex flex-col gap-1.5'>
    <Label>{label}</Label>
    <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoFocus={autoFocus} maxLength={120} className='h-11 rounded-full border border-green-deep03/20 px-4 text-[14px] text-text-primary placeholder:text-text-placeholder focus:border-green-deep02 focus:outline-none' />
  </label>
);
const Text = ({ label, value, onChange, placeholder, autoFocus }) => (
  <label className='flex flex-col gap-1.5'>
    <Label>{label}</Label>
    <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoFocus={autoFocus} rows={3} maxLength={4000} className='resize-y rounded-xl border border-green-deep03/20 px-3.5 py-2.5 text-[14px] leading-[150%] text-text-primary placeholder:text-text-placeholder focus:border-green-deep02 focus:outline-none' />
  </label>
);
const Note = ({ warn, children }) => (
  <div role='note' className={cn('flex gap-2.5 rounded-xl border px-4 py-3', warn ? 'border-[#ffdf56] bg-functional-warning01' : 'border-green-deep03/[0.12] bg-surface-green10')}>
    <span className={cn('mt-2 h-1.5 w-1.5 shrink-0 rounded-full', warn ? 'bg-functional-warning03' : 'bg-green-deep02')} />
    <span className={cn('text-[13px] leading-[150%] [text-wrap:pretty]', warn ? 'text-functional-warning04' : 'text-text-secondary')}>{children}</span>
  </div>
);
const Err = ({ text }) => (text ? <p className='text-[13px] text-functional-failed04'>{text}</p> : null);

/** 대화상자 틀 — Close + 실행(why 가 있으면 꺼짐 + 툴팁) */
const Dlg = ({ title, desc, onClose, go, why, busy, danger, onGo, children }) => (
  <Modal
    open
    title={title}
    subtitle={desc}
    onClose={onClose}
    width='max-w-[480px]'
    sheetOnMobile
    footer={
      <>
        <PillButton size='l' onClick={onClose}>
          Close
        </PillButton>
        <PillButton size='l' variant={danger ? 'dangerFill' : 'primary'} disabled={!!why || busy} tip={why} onClick={onGo}>
          {go}
        </PillButton>
      </>
    }
  >
    {children}
  </Modal>
);

const personOpt = (team) => (m) => ({ v: m.id, l: `${m.name} · ${JOB_LABEL[m.job] ?? m.job}${team?.memberRow(m.id)?.presence.kind === 'offline' ? ' · offline' : ''}` });

/* ── Call ───────────────────────────────────────────── */

export const CallDialog = ({ row, onAct, onClose }) => {
  const who = row.assigneeName;
  const [text, setText] = useState(`Hi ${firstName(who)}, “${row.t.title}” is waiting for you to accept or decline. Can you check it now?`);
  const { busy, error, submit } = useSubmit(onAct, onClose);
  return (
    <Dlg title={`Call ${who}`} desc={`Posts a question comment to ${firstName(who)} on this task. The team sees it like any question.`} onClose={onClose} go='Post question' why={text.trim() ? '' : 'Write a message'} busy={busy} onGo={() => submit({ type: 'call', taskId: row.t.id, memberId: row.t.assigneeId, body: text.trim() })}>
      <Cur rows={[['Task', row.t.title], ['Waiting', row.wait ? dur(row.wait.min) : '–']]} />
      <Text label='Message' value={text} onChange={setText} placeholder='Ask one clear question' autoFocus />
      <Err text={error} />
    </Dlg>
  );
};

export const PeerCallDialog = ({ member, label, onAct, onClose }) => {
  const [text, setText] = useState(`Hi ${firstName(member.name)}, your peer review is due soon. Can you submit it now?`);
  const { busy, error, submit } = useSubmit(onAct, onClose);
  return (
    <Dlg title={`Call ${member.name}`} desc={`Posts a question to ${firstName(member.name)} on the final deliverable. The team sees it like any question.`} onClose={onClose} go='Post question' why={text.trim() ? '' : 'Write a message'} busy={busy} onGo={() => submit({ type: 'peer_call', memberId: member.id, body: text.trim() })}>
      <Cur rows={[['Peer review', label || 'Not submitted']]} />
      <Text label='Message' value={text} onChange={setText} placeholder='Ask one clear question' autoFocus />
      <Err text={error} />
    </Dlg>
  );
};

/* ── Change due ─────────────────────────────────────── */

export const DueDialog = ({ row, endAt, onAct, onClose }) => {
  const opts = dueOptions(row.t, endAt);
  const [pick, setPick] = useState('');
  const [reason, setReason] = useState('');
  const [desc, setDesc] = useState('');
  const { busy, error, submit } = useSubmit(onAct, onClose);
  const why = !opts.length ? 'No room before the project end' : !pick || !reason || !desc.trim() ? 'Pick a due, a reason type and say what happened' : '';
  return (
    <Dlg title='Change due' desc='Only for an external reason. The task leaves the on-time count.' onClose={onClose} go='Change due' why={why} busy={busy} onGo={() => submit({ type: 'due', taskId: row.t.id, dueAt: new Date(Number(pick)).toISOString(), reason, description: desc.trim() })}>
      <Cur rows={[['Current due', kstAt(row.t.dueAt)], ...(row.adjusted ? [['Adjusted due', kstAt(row.adjusted)]] : [])]} />
      <SelectPill label='New due' value={pick} onChange={setPick} placeholder='Pick a new due' disabled={!opts.length} options={opts.map((o) => ({ v: String(o.at), l: `${kstAt(o.at)} (+${dur((o.at - new Date(row.t.dueAt).getTime()) / 60_000)})` }))} />
      <RadioList label='Reason type' value={reason} onChange={setReason} options={DUE_REASONS} />
      <Text label='What happened' value={desc} onChange={setDesc} placeholder='Say what blocked the work' />
      <Note warn>Delays from an earlier stage aren’t a reason here — the adjusted due already covers them.</Note>
      <Err text={error} />
    </Dlg>
  );
};

/* ── Reassign ───────────────────────────────────────── */

export const ReassignDialog = ({ row, team, onAct, onClose }) => {
  const cands = reassignCandidates(row, team.data.members);
  const noResp = !!row.wait && !row.wait.paused && row.wait.min >= ACCEPT_FAIL_MIN;
  const [to, setTo] = useState('');
  const [reason, setReason] = useState(noResp ? 'Accept no response' : '');
  const { busy, error, submit } = useSubmit(onAct, onClose);
  const why = !cands.length ? 'No eligible member' : !to || !reason.trim() ? 'Pick a teammate and add a reason' : '';
  const go = async () => {
    if (noResp) await onAct({ type: 'no_response', taskId: row.t.id }, { quiet: true });
    submit({ type: 'reassign', taskId: row.t.id, memberId: to, reason: reason.trim() });
  };
  return (
    <Dlg title='Reassign task' desc='The new assignee gets a request to accept. The accept wait starts again.' onClose={onClose} go='Reassign' why={why} busy={busy} onGo={go}>
      <Cur rows={[['Task', row.t.title], ['Now', row.assigneeName]]} />
      <SelectPill label='New assignee' value={to} onChange={setTo} placeholder={cands.length ? 'Pick a teammate' : 'No eligible member'} disabled={!cands.length} options={cands.map(personOpt(team))} />
      <Text label='Reason' value={reason} onChange={setReason} placeholder='Why this teammate' />
      <Note warn={noResp}>{noResp ? `Accept no response is recorded for ${row.assigneeName}.` : 'If they need more time, change the due next with Member dropout.'}</Note>
      <Err text={error} />
    </Dlg>
  );
};

/* ── Change receiver ────────────────────────────────── */

export const ReceiverDialog = ({ row, team, onAct, onClose }) => {
  const cands = receiverCandidates(row, team.data.members).map(personOpt(team));
  if (!row.toOps) cands.push({ v: 'npc', l: 'Team lead Kim · Lead inbox' });
  const [to, setTo] = useState('');
  const [reason, setReason] = useState('');
  const { busy, error, submit } = useSubmit(onAct, onClose);
  const why = !cands.length ? 'No eligible member' : !to || !reason.trim() ? 'Pick a receiver and add a reason' : '';
  return (
    <Dlg title='Change receiver' desc='The new receiver reviews the next submission.' onClose={onClose} go='Change receiver' why={why} busy={busy} onGo={() => submit({ type: 'receiver', taskId: row.t.id, memberId: to === 'npc' ? null : to, reason: reason.trim() })}>
      <Cur rows={[['Task', row.t.title], ['Now', row.receiverName]]} />
      <SelectPill label='New receiver' value={to} onChange={setTo} placeholder='Pick a receiver' options={cands} />
      <Text label='Reason' value={reason} onChange={setReason} placeholder='Why the change' />
      <Err text={error} />
    </Dlg>
  );
};

/* ── Cancel task ────────────────────────────────────── */

export const CancelDialog = ({ row, onAct, onClose }) => {
  const [reason, setReason] = useState('');
  const { busy, error, submit } = useSubmit(onAct, onClose);
  return (
    <Dlg title='Cancel this task?' desc='The assignee and receiver are notified. You can’t undo this.' onClose={onClose} go='Cancel task' danger why={reason.trim() ? '' : 'Add a reason'} busy={busy} onGo={() => submit({ type: 'cancel', taskId: row.t.id, reason: reason.trim() })}>
      <Cur rows={[['Task', row.t.title], ['Assignee', row.assigneeName]]} />
      <Text label='Reason' value={reason} onChange={setReason} placeholder='Why it’s cancelled' autoFocus />
      <Err text={error} />
    </Dlg>
  );
};

/* ── Mark as dropped ────────────────────────────────── */

export const DropDialog = ({ member, openTasks = 0, onAct, onClose }) => {
  const [rule, setRule] = useState('');
  const { busy, error, submit } = useSubmit(onAct, onClose);
  return (
    <Dlg title={`Mark ${member.name} as dropped?`} desc='Only ops decide this. The platform never marks anyone as dropped.' onClose={onClose} go='Mark as dropped' danger why={rule ? '' : 'Pick the rule that applies'} busy={busy} onGo={() => submit({ type: 'drop', memberId: member.id, rule, body: '' })}>
      <RadioList label='Which rule applies' value={rule} onChange={setRule} options={DROP_RULES} />
      <Note warn>
        Dropped members don’t complete the project, get no report and leave peer review. Their data stays for ops.
        {openTasks ? ` ${openTasks} open task${openTasks > 1 ? 's' : ''} will need reassigning.` : ''}
      </Note>
      <Err text={error} />
    </Dlg>
  );
};

/* ── Send announcement (전체 공지) ───────────────────── */

export const PublishDialog = ({ teamId, teamName, onAct, onClose }) => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const { busy, error, submit } = useSubmit(onAct, onClose);
  return (
    <Dlg title='Send announcement' desc={`${teamId ? `Everyone on Team ${teamName}` : 'Every member of every team'} gets this as an Inbox notification. Long announcements open in full when tapped.`} onClose={onClose} go='Send' why={title.trim() && body.trim() ? '' : 'Add a title and details'} busy={busy} onGo={() => submit({ type: 'publish', teamId: teamId ?? null, title: title.trim(), body: body.trim() })}>
      <Input label='Title' value={title} onChange={setTitle} placeholder='What changed, in a few words' autoFocus />
      <Text label='Details' value={body} onChange={setBody} placeholder='What the team should do differently' />
      <Err text={error} />
    </Dlg>
  );
};

/* ── Add note (ops only) ────────────────────────────── */

export const NoteDialog = ({ teamId, taskId, member, onAct, onClose }) => {
  const [body, setBody] = useState('');
  const { busy, error, submit } = useSubmit(onAct, onClose);
  return (
    <Dlg title={`Add note${member ? ` · ${member.name}` : ''}`} desc='Ops only. Time is recorded automatically.' onClose={onClose} go='Save note' why={body.trim() ? '' : 'Write a note'} busy={busy} onGo={() => submit({ type: 'note', teamId: teamId ?? null, taskId: taskId ?? null, memberId: member?.id ?? null, body: body.trim() })}>
      <Text label='Note' value={body} onChange={setBody} placeholder='What did you see?' autoFocus />
      <Err text={error} />
    </Dlg>
  );
};
